import { useCallback, useMemo } from "react";
import { ALERT_TYPES_MAP } from "../const";
import {
  AlertType,
  AlertTypeData,
  IConfiguredAlertGridItem,
  ISubscriptionItem,
  SubscriptionHashes
} from "../@types";
import { alertfeed } from "../../../shim/jsbridge";
import { shouldUseSampleAvailableAlertType } from "../utils";

const { ALERT_TYPE } = alertfeed;

const useAlertTypes = () => {
  const getAlertType = useMemo(
    () =>
      (alertTypeKey: AlertType): AlertTypeData | null => {
        const alertTypes = Object.values(ALERT_TYPE) as AlertTypeData[];
        return alertTypes.find(value => value.type == alertTypeKey) || null;
      },
    []
  );

  const getAlertTypeLabel = useMemo(
    () =>
      (alertTypeKey: AlertType): string | null => {
        const alertType = getAlertType(alertTypeKey);
        return alertType?.label || null;
      },
    [getAlertType]
  );

  const getAlertTypeHashValueSetup = useMemo(
    () =>
      (
        alertTypeKey: AlertType,
        feature: keyof Omit<AlertTypeData, "type" | "label">
      ): boolean => {
        const alertType = getAlertType(alertTypeKey);
        return alertType ? alertType[feature] || false : false;
      },
    [getAlertType]
  );

  const getAllAlertTypes = useMemo(
    () => (): AlertTypeData[] => {
      return Object.values(ALERT_TYPE) as AlertTypeData[];
    },
    []
  );

  const getAlertEventType = (
    data: IConfiguredAlertGridItem,
    type: AlertType
  ): {
    subscription: ISubscriptionItem | undefined;
    hasSubscription: boolean;
  } => {
    const targetedTypeSubscription = data.subscriptions.find(
      sub => sub.type === type
    );
    return {
      subscription: targetedTypeSubscription,
      hasSubscription: !!targetedTypeSubscription
    };
  };

  const getHashValueSupportedAlertTypes = useCallback(
    (subscriptionHashes: SubscriptionHashes): string[] => {
      const alertTypes = Object.values(ALERT_TYPE) as AlertTypeData[];
      const hashSupportedAlertTypes = alertTypes
        .filter(alertType => alertType.supports_hash_values)
        .map(item => item.type);

      return shouldUseSampleAvailableAlertType(subscriptionHashes)
        ? hashSupportedAlertTypes
        : hashSupportedAlertTypes.filter(
            alert => alert !== ALERT_TYPES_MAP.SAMPLE_AVAILABLE
          );
    },
    []
  );

  return {
    getAlertType,
    getAlertTypeLabel,
    getAlertTypeHashValueSetup,
    getAllAlertTypes,
    getHashValueSupportedAlertTypes,
    getAlertEventType
  };
};

export default useAlertTypes;
